'use client';

import { projectPath } from "@/lib/routes";
import { useContentSize } from "@/components/SizeProvider";
import { formatDesktopTitle } from "@/lib/utils/typography";
import DraggableWindow from "@/components/DraggableWindow";
import GrabCursorLabel from "@/components/GrabCursorLabel";
import { useEffect, useMemo, useRef } from "react";
import { useLenis } from "lenis/react";
import VideoPlayer from "@/components/VideoPlayer";
import styles from './home-content.module.scss';
import { useZIndexManager } from "@/lib/hooks/useZIndexManager";
import { useCollapseNavigate } from "@/lib/hooks/useCollapseNavigate";
import { ProjectModel } from "@/lib/models/Project";
import { useAmbientScramble } from '@/lib/hooks/useAmbientScramble';
import {
  PROJECT_THUMBNAIL_ASPECT_RATIO,
  SHOWREEL_VIDEO_ASPECT_RATIO,
} from "@/lib/constants/media";
import { getWindowSize } from "@/lib/constants/window";
import { REFERENCE_CONTENT_HEIGHT, REFERENCE_CONTENT_WIDTH } from "@/lib/utils/contentSize";

interface Props {
  projects: ProjectModel[];
}

const PROJECT_WINDOW_WIDTH_RATIO = 0.234;
const SHOWREEL_WIDTH_RATIO = 0.5;
const SHOWREEL_TOP_RATIO = 0.15;
const FIRST_WINDOW_TOP_RATIO = 0.85;
const PAGE_BOTTOM_BUFFER_RATIO = 0.2;

const BACKDROP_REFERENCE_WIDTH = 1440;
const BACKDROP_CENTER_OFFSET = 229;
const BACKDROP_TITLE_FONT_SIZE = 186;
const BACKDROP_SUBTITLE_FONT_SIZE = 40;
const BACKDROP_SUBTITLE_MARGIN_TOP = 28;

// Since our desktop aspect ratio is 18 / 9, on a FHD display, the size would be 1920 x 960.
// Define window preset positions
const WINDOW_POSITIONING = {
  // Window positions will cycle through this set of positions
  // Note: x position is based off a reference widthContent of 1920px, whereas y position represents the vertical offset from the first draggable window, based on a reference content height of 960px.
  presets: [
    { x: 437, y: 0 },
    { x: 1025, y: 20 },
    { x: 733, y: 258 },
    { x: 970, y: 530 },
    { x: 442, y: 604 },
    { x: 927, y: 864 },
  ],
  // Spacing between each set of preset positions
  verticalSpacing: 1200,
};

export default function HomeContent({ projects }: Props) {
  const { width: widthContent, height: heightContent } = useContentSize();
  const lenis = useLenis();
  const homeContent = useRef<HTMLDivElement>(null);
  const homeContentHeightRef = useRef(0);
  const { getZIndex, handleElementClick } = useZIndexManager();
  const { isCollapsing, navigate } = useCollapseNavigate();
  const asteriskRef = useRef<HTMLSpanElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Project window is 23.4% of the content width; height is that width at the 674:1200 thumbnail ratio plus the title bar.
  const draggableWindowSize = getWindowSize(widthContent, PROJECT_WINDOW_WIDTH_RATIO, PROJECT_THUMBNAIL_ASPECT_RATIO);

  // Backdrop sizes are authored against a 1440px content width, so every backdrop px value is multiplied by this.
  const backdropScale = widthContent / BACKDROP_REFERENCE_WIDTH;

  // Showreel is half the content width; height is that width at 16:9 plus the title bar.
  const videoPlayerSize = getWindowSize(widthContent, SHOWREEL_WIDTH_RATIO, SHOWREEL_VIDEO_ASPECT_RATIO);

  const getInitialPosition = (index: number) => {
    const presetPosition = WINDOW_POSITIONING.presets[index % WINDOW_POSITIONING.presets.length];
    const presetCycle = Math.floor(index / WINDOW_POSITIONING.presets.length); // Cycle 0 = first set of presets -> no need to add vertical spacing.

    const leftOffset = (window.innerWidth - widthContent) / 2;
    const positionX = presetPosition.x / REFERENCE_CONTENT_WIDTH * widthContent + leftOffset;
    const offsetY = (presetPosition.y + presetCycle * WINDOW_POSITIONING.verticalSpacing) / REFERENCE_CONTENT_HEIGHT * heightContent;
    const topOffset = (window.innerHeight - heightContent) / 2;
    const positionY = topOffset + heightContent * FIRST_WINDOW_TOP_RATIO + offsetY;

    return {
      x: positionX,
      y: positionY
    };
  };

  const presetPositions = useMemo<{ x: number; y: number }[] | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    return projects.map((_, index) => getInitialPosition(index));
  }, [widthContent, heightContent, projects.length]);

  const showreelPosition = useMemo<{ x: number; y: number } | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    return {
      x: window.innerWidth * 0.50 - videoPlayerSize.width / 2,
      y: (window.innerHeight - heightContent) / 2 + heightContent * SHOWREEL_TOP_RATIO,
    };
  }, [widthContent, heightContent, videoPlayerSize.width]);

  // Adjust homeContent minHeight
  useEffect(() => {
    if (widthContent === 0 || heightContent === 0 || !homeContent.current || projects.length === 0) {
      return;
    }

    const lastIndex = projects.length - 1;
    const lastPosition = getInitialPosition(lastIndex);

    const height = lastPosition.y + draggableWindowSize.height + window.innerHeight * PAGE_BOTTOM_BUFFER_RATIO;
    homeContent.current.style.height = `${height}px`;
    homeContentHeightRef.current = height;

    lenis?.resize();
  }, [widthContent, heightContent, homeContent.current, projects.length, draggableWindowSize.height]);

  useAmbientScramble(backdropRef, { selector: '.backdrop-scrambler' });

  useEffect(() => {
    let cancelled = false;

    document.fonts.ready.then(() => {
      const asterisk = asteriskRef.current;
      const ctx = document.createElement('canvas').getContext('2d');
      if (cancelled || !asterisk || !ctx) return;

      const style = getComputedStyle(asterisk);
      const fontSize = parseFloat(style.fontSize);
      const lineHeight = parseFloat(style.lineHeight);
      if (!fontSize || !lineHeight) return;

      ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      ctx.fontStretch = 'condensed';
      const metrics = ctx.measureText('*');

      const contentHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
      const baseline = (lineHeight - contentHeight) / 2 + metrics.fontBoundingBoxAscent;
      const originX = (metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft) / 2;
      const originY = baseline - (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;
      asterisk.style.transformOrigin = `${originX / fontSize}em ${originY / fontSize}em`;
    });

    return () => {
      cancelled = true;
    };
  }, [widthContent]);

  // Assumes draggable's pivot point is at top left
  const constrainDraggableToViewport = (pos: { x: number; y: number }, size: { width: number; height: number }) => {
    const isHomeContentHeightMeasured = homeContentHeightRef.current > 0;
    const contentHeight = isHomeContentHeightMeasured ? homeContentHeightRef.current : document.documentElement.clientHeight;
    const constrainedX = Math.max(-size.width / 2, Math.min(pos.x, window.innerWidth - size.width / 2));
    const constrainedY = Math.min(Math.max(-size.height / 2, pos.y), contentHeight - size.height / 2);
    return { x: constrainedX, y: constrainedY };
  };

  return (
    <div className={styles['home-content']} ref={homeContent}>
      <div
        ref={backdropRef}
        className={styles['backdrop']}
        style={{ top: `calc(50% - ${BACKDROP_CENTER_OFFSET * backdropScale}px)` }}
      >
        <p className={styles['backdrop-title']} style={{ fontSize: BACKDROP_TITLE_FONT_SIZE * backdropScale }}>
          <span className="backdrop-scrambler">MILDLY</span>
          <br />
          <span className="backdrop-scrambler">DANGEROUS</span>
          <span className={styles['backdrop-asterisk']} ref={asteriskRef}>*</span>
          <br />
          <span className="backdrop-scrambler">THINKING</span>
        </p>
        <p
          className={styles['backdrop-subtitle']}
          style={{ fontSize: BACKDROP_SUBTITLE_FONT_SIZE * backdropScale, marginTop: BACKDROP_SUBTITLE_MARGIN_TOP * backdropScale }}
        >
          *<span className="backdrop-scrambler">HANDLE WITH CURIOSITY</span>
        </p>
      </div>
      {projects
        .map((project, index) => {
          const id = `project-${index}`;
          if (project.projectMedia.length === 0) {
            return null;
          }
          return (
            <DraggableWindow
              key={index}
              title={`${(index + 1).toString().padStart(2, '0')}\\${formatDesktopTitle(project.projectTitle)}`}
              src={project.projectMedia[0]}
              initialPosition={presetPositions?.[index] ?? { x: 0, y: 0 }}
              size={draggableWindowSize}
              mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
              allowDragging={true}
              constrainingFunction={constrainDraggableToViewport}
              href={projectPath(project.slug)}
              zIndex={getZIndex(id)}
              onPointerDownCapture={() => handleElementClick(id)}
              widthContent={widthContent}
              isCollapsing={isCollapsing}
              onNavigate={navigate}
              autoplay
              eager={index === 0}
            />
          );
        })
      }
      <VideoPlayer
        title="THE_OTHER_LAB_SHOWREEL_2025"
        src="/assets/video/Showreel_fixed.mp4"
        initialPosition={showreelPosition ?? { x: 0, y: 0 }}
        size={videoPlayerSize}
        allowDragging={true}
        constrainingFunction={constrainDraggableToViewport}
        zIndex={getZIndex('showreel')}
        onPointerDownCapture={() => handleElementClick('showreel')}
        widthContent={widthContent}
        isCollapsing={isCollapsing}
        eager={false}
        autoplayOnIdle
        poster="/assets/image/BG.png"
        mediaAspectRatio={SHOWREEL_VIDEO_ASPECT_RATIO}
      />
      <GrabCursorLabel />
    </div>
  )
}
